/* eslint-disable max-statements */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { add, sub } from "date-fns";
import { useSearchParams } from "react-router-dom";

import { getIncrementUnit, getQueryDate, queryDateFormat } from "../../../helpers/dates";
import { getAllowedView } from "../../../helpers/event";

const CourseFiltersContext = React.createContext(null);

// eslint-disable-next-line react/prop-types
export const ClassFilterProvider = ({ children }) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [updateQuery, setUpdateQuery] = useState(true);
	const [updateViewDate, setUpdateViewDate] = useState(false);

	const getLocalFacets = useCallback(() => {
		const localFacets = window.localStorage.getItem("eventsFacet");
		if (localFacets) {
			return JSON.parse(localFacets);
		}
		return {};
	}, []);

	const setMySearchParams = useCallback(async (newParams) => {
		const curParams = {};

		if (searchParams.get("date")) {
			curParams.date = searchParams.get("date");
		}

		if (searchParams.get("view")) {
			curParams.view = searchParams.get("view");
		}

		if (searchParams.get("search")) {
			curParams.search = searchParams.get("search");
		}

		if (searchParams.get("facets")) {
			curParams.facets = searchParams.get("facets");
		}

		if (newParams.facets && Object.keys(newParams.facets).length === 0) {
			delete newParams.facets;
			delete curParams.facets;
		}

		await setUpdateViewDate(() => false);
		setSearchParams({
			...curParams,
			...newParams
		});
		await setUpdateViewDate(() => true);
	}, [searchParams]);

	const [view, setView] = useState(getAllowedView(searchParams.get("view")));
	useEffect(() => {
		if (updateQuery) {
			setMySearchParams({ view });
		}
	}, [view]);

	const [date, setDate] = useState(getQueryDate(searchParams.get("date")));
	useEffect(() => {
		if (updateQuery) {
			setMySearchParams({
				date: queryDateFormat(date)
			});
		}
	}, [date]);

	const setDateView = useCallback(async (newDate, newView) => {
		await setUpdateQuery(() => false);
		await setDate(() => newDate);
		await setView(() => newView);
		setMySearchParams({
			date: queryDateFormat(newDate),
			view: newView
		});
		await setUpdateQuery(() => true);
	}, [setMySearchParams]);

	useEffect(async () => {
		if (updateViewDate) {
			await setUpdateQuery(() => false);
			if (searchParams.get("date")) {
				await setDate(() => getQueryDate(searchParams.get("date")));
			}

			if (searchParams.get("view")) {
				await setView(() => searchParams.get("view"));
			} else {
				await setView(() => "month");
			}

			await setUpdateQuery(() => true);
		}
	}, [searchParams]);

	const nextIncrement = useCallback(async () => {
		const nextDate = add(date, { [getIncrementUnit(view)]: 1 });
		await setDate(() => nextDate);
	}, [date, view]);

	const prevIncrement = useCallback(async () => {
		const prevDate = sub(date, { [getIncrementUnit(view)]: 1 });
		await setDate(() => prevDate);
	}, [date, view]);

	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState({});
	const [facets, setFacets] = useState([]);

	useEffect(() => {
		if (filters && filters.facets && Object.keys(filters.facets).length) {
			setMySearchParams({ facets: JSON.stringify(filters.facets) });
		} else {
			setMySearchParams({ facets: {} });
		}
	}, [filters.facets]);

	const [allFacets, setAllFacets] = useState([]);
	const [facetValues, setFacetValues] = useState(getLocalFacets());

	const toggleFilters = useCallback(() => setShowFilters((cur) => !cur), [setShowFilters]);

	// eslint-disable-next-line object-property-newline
	const setNewFilters = useCallback((newFilters) => setFilters((oldFilters) => ({ ...oldFilters, ...newFilters })), [setFilters]);

	const handleFacetChange = useCallback((newFacetValues) => {
		setFilters((oldFilters) => ({
			...oldFilters,
			facets: newFacetValues
		}));
	}, [setFilters]);

	useEffect(() => {
		const loadFacets = searchParams.get("facets");
		if (loadFacets) {
			setFacetValues(JSON.parse(loadFacets));
		}
	}, [searchParams, setFacetValues]);

	const isFiltered = useMemo(() => Object.values(facetValues).length, [facetValues]);

	const providerReturn = useMemo(() => ({
		facetValues,
		setFacetValues,
		facets,
		setFacets,
		handleFacetChange,
		filters,
		setFilters: setNewFilters,
		allFacets,
		setAllFacets,
		showFilters,
		setShowFilters,
		toggleFilters,
		isFiltered,
		view,
		setView,
		date,
		setDate,
		nextIncrement,
		prevIncrement,
		setDateView,
		setMySearchParams
	}), [facets, facetValues, filters, showFilters, view, date, allFacets]);

	return (
		<CourseFiltersContext.Provider value={providerReturn}>
			{children}
		</CourseFiltersContext.Provider>
	);
};

export const useClassFilters = () => React.useContext(CourseFiltersContext);
